/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Bell, X, Trophy, XCircle, Zap, CheckCircle } from 'lucide-react';
import { apiClient } from '../../helpers';

interface Notification {
  id: number;
  type: 'challenge_sent' | 'challenge_rejected' | 'challenge_completed' | 'challenge_accepted';
  data: any;
  read: boolean;
  createdAt: string;
  challengeId?: number;
}

const BellContainer = styled.div`
  position: relative;
`;

const BellButton = styled.button`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(102, 126, 234, 0.1);
  }

  svg {
    color: #667eea;
    width: 24px;
    height: 24px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  background: #ff6b6b;
  color: white;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  box-shadow: 0 2px 4px rgba(255, 107, 107, 0.4);
`;

const DropdownOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  max-height: 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  overflow: hidden;

  @media (max-width: 768px) {
    width: 280px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }
`;

const MarkAllReadButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const NotificationList = styled.div`
  max-height: 340px;
  overflow-y: auto;
`;

const NotificationItem = styled.div<{ $read: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: ${props => props.$read ? 'white' : 'rgba(102, 126, 234, 0.05)'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(102, 126, 234, 0.08);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div<{ $type: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  ${props => {
    switch (props.$type) {
      case 'challenge_sent':
        return `
          background: linear-gradient(135deg, #e3f2fd 0%, #2196f3 100%);
          color: #0d47a1;
        `;
      case 'challenge_rejected':
        return `
          background: linear-gradient(135deg, #fee 0%, #fdd 100%);
          color: #c33;
        `;
      case 'challenge_completed':
        return `
          background: linear-gradient(135deg, #fff3cd 0%, #ffc107 100%);
          color: #856404;
        `;
      case 'challenge_accepted':
        return `
          background: linear-gradient(135deg, #d4edda 0%, #28a745 100%);
          color: #155724;
        `;
      default:
        return `
          background: #f0f0f0;
          color: #666;
        `;
    }
  }}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
`;

const NotificationMessage = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`;

const NotificationTime = styled.div`
  font-size: 10px;
  color: #999;
  margin-top: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #999;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const ResultBadge = styled.span<{ $isWinner: boolean }>`
  display: inline-block;
  padding: 2px 6px;
  background: ${props => props.$isWinner ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$isWinner ? '#155724' : '#721c24'};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  margin-top: 4px;
`;

interface NotificationBellProps {
  studentId?: number;
  currentStudentId?: number;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ studentId, currentStudentId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (!studentId) return;
    fetchNotifications();
  }, [studentId]);

  const fetchNotifications = async () => {
    if (!studentId) return;
    
    try {
      setLoading(true);
      // Fetch all notification types
      const [sentRes, rejectedRes, completedRes] = await Promise.all([
        apiClient.get(`/api/notifications/${studentId}?type=challenge_sent`),
        apiClient.get(`/api/notifications/${studentId}?type=challenge_rejected`),
        apiClient.get(`/api/notifications/${studentId}?type=challenge_completed`),
      ]);

      const allNotifications: Notification[] = [
        ...(sentRes.data || []),
        ...(rejectedRes.data || []),
        ...(completedRes.data || []),
      ];

      // Sort by createdAt descending
      allNotifications.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setNotifications(allNotifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(n => 
          apiClient.patch(`/api/notifications/${n.id}/read`)
        )
      );
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await apiClient.patch(`/api/notifications/${notification.id}/read`);
        setNotifications(notifications.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'challenge_sent':
        return <Zap />;
      case 'challenge_rejected':
        return <XCircle />;
      case 'challenge_completed':
        return <Trophy />;
      case 'challenge_accepted':
        return <CheckCircle />;
      default:
        return <Bell />;
    }
  };

  const getNotificationTitle = (notification: Notification) => {
    const data = notification.data || {};
    switch (notification.type) {
      case 'challenge_sent': {
        const gameTypeText = data.gameType === 'quiz' ? 'Trắc nghiệm' : 'Ghép thẻ';
        return `🎮 ${data.challengerName || 'Ai đó'} thách đấu ${gameTypeText}`;
      }
      case 'challenge_rejected':
        return data.rejectorName ? `❌ ${data.rejectorName} từ chối thách đấu` : '❌ Thách đấu bị từ chối';
      case 'challenge_completed': {
        // Xác định đối thủ
        const opponentName = currentStudentId === data.challengerId ? data.accepterName : data.challengerName;
        const isWinner = data.winner === (currentStudentId === data.challengerId ? 'challenger' : 'accepter');
        const giftAmount = data.giftAmount || 0;
        if (data.winner === 'tie') {
          return opponentName ? `🤝 Hoà với ${opponentName}` : '🤝 Hoà';
        }
        if (isWinner) {
          return opponentName ? `🎉 Thắng ${opponentName}! +${giftAmount}⭐` : `🎉 Bạn thắng! +${giftAmount}⭐`;
        }
        return opponentName ? `😔 Thua ${opponentName}! -${giftAmount}⭐` : `😔 Bạn thua! -${giftAmount}⭐`;
      }
      case 'challenge_accepted':
        return data.accepterName ? `✅ ${data.accepterName} chấp nhận thách đấu` : '✅ Thách đấu được chấp nhận';
      default:
        return 'Thông báo';
    }
  };

  const getNotificationMessage = (notification: Notification) => {
    const data = notification.data || {};
    switch (notification.type) {
      case 'challenge_sent': {
        const gameTypeText = data.gameType === 'quiz' ? 'Trắc nghiệm' : 'Ghép thẻ';
        return `${data.challengerName || 'Ai đó'} thách đấu ${gameTypeText} - ${data.setName || 'bộ từ'} - với ${data.giftAmount || 0} sao.`;
      }
      case 'challenge_rejected':
        return `${data.rejectorName || 'Ai đó'} đã từ chối lời thách đấu của bạn.`;
      case 'challenge_completed': {
        const isWinner = data.winner === (currentStudentId === data.challengerId ? 'challenger' : 'accepter');
        const opponentName = currentStudentId === data.challengerId ? data.accepterName : data.challengerName;
        const giftAmount = data.giftAmount || 0;
        if (data.winner === 'tie') {
          return `Hoà với ${opponentName || 'đối thủ'}! Giữ nguyên ${giftAmount} sao.`;
        }
        if (isWinner) {
          return `Bạn thắng ${opponentName || 'đối thủ'}! Nhận thêm ${giftAmount} sao.`;
        }
        return `Bạn thua ${opponentName || 'đối thủ'}! Mất ${giftAmount} sao.`;
      }
      case 'challenge_accepted':
        return `${data.accepterName || 'Ai đó'} đã chấp nhận lời thách đấu của bạn. Hãy chờ họ hoàn thành!`;
      default:
        return '';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;
    if (diffHours < 24) return `${diffHours} giờ trước`;
    if (diffDays < 7) return `${diffDays} ngày trước`;
    return date.toLocaleDateString('vi-VN');
  };

  return (
    <BellContainer ref={dropdownRef}>
      <BellButton onClick={handleBellClick}>
        <Bell />
        {unreadCount > 0 && <Badge>{unreadCount > 9 ? '9+' : unreadCount}</Badge>}
      </BellButton>

      <DropdownOverlay $isOpen={isOpen} onClick={() => setIsOpen(false)} />
      
      <Dropdown $isOpen={isOpen}>
        <DropdownHeader>
          <h3>🔔 Thông báo</h3>
          {unreadCount > 0 && (
            <MarkAllReadButton onClick={handleMarkAllRead}>
              Đánh dấu đã đọc
            </MarkAllReadButton>
          )}
        </DropdownHeader>

        <NotificationList>
          {loading ? (
            <EmptyState>
              <p>Đang tải...</p>
            </EmptyState>
          ) : notifications.length === 0 ? (
            <EmptyState>
              <Bell />
              <p>Không có thông báo nào</p>
            </EmptyState>
          ) : (
            notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                $read={notification.read}
                onClick={() => handleNotificationClick(notification)}
              >
                <NotificationIcon $type={notification.type}>
                  {getNotificationIcon(notification.type)}
                </NotificationIcon>
                <NotificationContent>
                  <NotificationTitle>{getNotificationTitle(notification)}</NotificationTitle>
                  <NotificationMessage>{getNotificationMessage(notification)}</NotificationMessage>
                  <NotificationTime>{formatTime(notification.createdAt)}</NotificationTime>
                </NotificationContent>
              </NotificationItem>
            ))
          )}
        </NotificationList>
      </Dropdown>
    </BellContainer>
  );
};

export default NotificationBell;
